import React, { useEffect, useRef, useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, CircularProgress, Button, Typography
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import { DocumentSnapshot } from "firebase/firestore";
import Loader from "../../Component/Loader";
import { CareerFormValues } from "../../Forms/CareerForm";
import getCareersFromFirebase, { CareerFormData, deleteCareerById } from "../../services/careerService";
import { useConfigStore } from "../../redux/hooks/useConfigStore";
// import getCareersFromFirebase, { deleteCareerById } from "../../services/CareerService";

interface AdminCareerTableProps {
  onEdit?: (career: CareerFormData & { id: string }) => void;
}

const AdminCareerTable = ({ onEdit }: AdminCareerTableProps) => {
  const [careers, setCareers] = useState<(CareerFormData)[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastVisible, setLastVisible] = useState<DocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [rowLoading, setRowLoading] = useState<string | null>(null);
  const isFetchingRef = useRef(false);
  const {storeCareer} = useConfigStore();

  useEffect(() => {
    fetchInitialCareers();
  }, []);

  const fetchInitialCareers = async () => {
    try {
      setLoading(true);
      const { careers: initial, lastVisible } = await getCareersFromFirebase(6);
      setCareers(initial);
      storeCareer(initial);
      setLastVisible(lastVisible);
      if (initial.length < 6) setHasMore(false);
    } catch (err) {
      toast.error("Failed to load careers.");
    } finally {
      setLoading(false);
    }
  };

  const fetchNextCareers = async () => {
    if (isFetchingRef.current || !hasMore) return;
    isFetchingRef.current = true;
    try {
      const { careers: next, lastVisible: newLastVisible } = await getCareersFromFirebase(6, lastVisible);
      setCareers((prev) => {
        const updated = [...prev, ...next];
        storeCareer(updated);
        return updated;
    });
      setLastVisible(newLastVisible);
      if (next.length < 6) setHasMore(false);
    } catch (err) {
      toast.error("Failed to load more careers.");
    } finally {
      isFetchingRef.current = false;
    }
  };

  const handleDelete = async (id: string) => {
    setRowLoading(id);
    try {
      await deleteCareerById(id);
      toast.success("Career deleted");
      setCareers(prev => prev.filter(career => career.id !== id));
    } catch (err) {
      toast.error("Failed to delete career");
    } finally {
      setRowLoading(null);
    }
  };

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Job Title</TableCell>
            <TableCell>Job Type</TableCell>
            <TableCell>Location</TableCell>
            <TableCell>Experience</TableCell>
            <TableCell>Salary</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={6}><Loader /></TableCell>
            </TableRow>
          ) : careers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6}><Typography align="center">No careers available</Typography></TableCell>
            </TableRow>
          ) : (
            careers.map(career => (
              <TableRow key={career.id}>
                <TableCell>{career.jobTitle}</TableCell>
                <TableCell>{career.jobType}</TableCell>
                <TableCell>{career.jobLocation}</TableCell>
                <TableCell>{career.experienceLevel}</TableCell>
                <TableCell>{career.salary}</TableCell>
                <TableCell>
                  <IconButton onClick={() => onEdit?.(career)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(career.id)} disabled={rowLoading === career.id}>
                    {rowLoading === career.id ? <CircularProgress size={20} /> : <DeleteIcon />}
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {hasMore && (
        <div className="text-center my-4">
          <Button variant="outlined" onClick={fetchNextCareers}>
            Load More
          </Button>
        </div>
      )}
    </TableContainer>
  );
};

export default AdminCareerTable;
